import { useState, useEffect, useMemo } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { ErrorBoundary } from './components';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TaskCreate from './pages/TaskCreate';
import TaskPage from './pages/TaskPage';
import TaskManagement from './pages/TaskManagement';
import MMCSearch from './pages/MMCSearch';
import SeriesEditor from './pages/SeriesEditor';
import SuperSeriesEditor from './pages/SuperSeriesEditor';
import PlateSearch from './pages/PlateSearch';
import DetectionSearch from './pages/DetectionSearch';
import MMCStatistics from './pages/MMCStatistics';
import MMCModelYearStatistics from './pages/MMCModelYearStatistics';
import PlateStatistics from './pages/PlateStatistics';
import DetectionStatistics from './pages/DetectionStatistics';
import MMCFlags from './pages/MMCFlags';
import DetectionFlags from './pages/DetectionFlags';
import PlateFlags from './pages/PlateFlags';
import MMCDatasets from './pages/MMCDatasets';
import DetectionDatasets from './pages/DetectionDatasets';
import PlateDatasets from './pages/PlateDatasets';
import { isAuthenticated, removeToken, removeUserData, getThemeMode } from './utils/cookies';
import { getPageFromURL } from './utils/urlParams';
import { PageName } from './types';

function App() {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [themeMode, setThemeModeState] = useState<'light' | 'dark'>(getThemeMode());

  const theme = useMemo(() => createTheme({ palette: { mode: themeMode } }), [themeMode]);
  const [currentPage, setCurrentPage] = useState<PageName>('dashboard');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    // Check if user is already authenticated on mount
    const authenticated = isAuthenticated();
    setLoggedIn(authenticated);

    // Check for page from URL params (for shareable search URLs)
    if (authenticated) {
      const pageFromURL = getPageFromURL();
      if (pageFromURL) {
        setCurrentPage(pageFromURL);
      }
    }

    // Listen for auth:logout event from API interceptor (token refresh failed)
    const handleAuthLogout = () => {
      setLoggedIn(false);
      setCurrentPage('dashboard');
    };

    const handleThemeChanged = () => {
      setThemeModeState(getThemeMode());
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    window.addEventListener('theme:changed', handleThemeChanged);
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
      window.removeEventListener('theme:changed', handleThemeChanged);
    };
  }, []);

  const handleLoginSuccess = (): void => {
    setLoggedIn(true);
    // Check for page from URL params (for shareable search URLs after login)
    const pageFromURL = getPageFromURL();
    if (pageFromURL) {
      setCurrentPage(pageFromURL);
    }
  };

  const handleLogout = (): void => {
    removeToken();
    removeUserData();
    setLoggedIn(false);
    setCurrentPage('dashboard');
  };

  const handleNavigate = (page: PageName, taskId?: string): void => {
    setCurrentPage(page);
    if (taskId) {
      setSelectedTaskId(taskId);
    }
  };

  const handleTaskCreated = (): void => {
    // Trigger a refresh of the dashboard data and navigate to task management
    setRefreshKey((prev) => prev + 1);
    setCurrentPage('taskManagement');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'taskCreate':
        return (
          <TaskCreate
            onBack={() => handleNavigate('dashboard')}
            onTaskCreated={handleTaskCreated}
          />
        );
      case 'taskPage':
        return selectedTaskId ? (
          <TaskPage
            taskId={selectedTaskId}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        ) : (
          <Dashboard
            key={refreshKey}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'taskManagement':
        return (
          <TaskManagement
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'searchMmc':
        return (
          <MMCSearch
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'seriesEditor':
        return (
          <SeriesEditor
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'superSeriesEditor':
        return (
          <SuperSeriesEditor
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'searchDet':
        return (
          <DetectionSearch
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'searchPlate':
        return (
          <PlateSearch
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'statsMmc':
        return (
          <MMCStatistics
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'statsMmcModelYear':
        return (
          <MMCModelYearStatistics
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'statsDet':
        return (
          <DetectionStatistics
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'statsPlate':
        return (
          <PlateStatistics
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'flagsMmc':
        return (
          <MMCFlags
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'flagsDet':
        return (
          <DetectionFlags
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'flagsPlate':
        return (
          <PlateFlags
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'datasetsMmc':
        return (
          <MMCDatasets
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'datasetsDet':
        return (
          <DetectionDatasets
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'datasetsPlate':
        return (
          <PlateDatasets
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
      case 'dashboard':
      default:
        return (
          <Dashboard
            key={refreshKey}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        {loggedIn ? renderPage() : <Login onLoginSuccess={handleLoginSuccess} />}
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
